import { useEffect, useRef } from 'react'
import { useArenaStore } from '../store'
import type { NormalizedRun, TranscriptEvent } from '../api/types'
import { INPUT_PRIORITY, modalInputRouter } from '../input/modalInputRouter'
import { runStatusColor } from '../pc/world/statusViewModels'

/** Risk level → UI-kit status color. */
export function riskColor(risk: string): string {
  const r = risk.toLowerCase()
  if (r === 'low') return '#a3b18a'
  if (r === 'medium') return '#fbbf24'
  if (r === 'high' || r === 'critical') return '#f87171'
  return '#94a3b8'
}

/** Gate decision verdict → UI-kit status color. */
export function decisionColor(decision: string): string {
  const d = decision.toLowerCase()
  if (d === 'advance' || d === 'approve' || d === 'pass') return '#22d3ee'
  if (d === 'rework' || d === 'retry') return '#fbbf24'
  if (d === 'fail' || d === 'abort' || d === 'block') return '#f87171'
  return '#94a3b8'
}

export function statusColor(status: string): string {
  return runStatusColor(status)
}

/** Portal base URL for the "Open in portal" deep link. */
export const PORTAL_BASE_URL = 'https://animus-launchapp-production.up.railway.app'

/** Deep link to a workflow run on the Animus portal. */
export function portalRunUrl(runId: string): string {
  return `${PORTAL_BASE_URL}/workflows/${encodeURIComponent(runId)}`
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—'
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`
  return `${m}m ${String(s % 60).padStart(2, '0')}s`
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function runDuration(run: NormalizedRun): string {
  if (typeof run.durationMs === 'number') return formatDuration(run.durationMs)
  const start = new Date(run.createdAt).getTime()
  const isTerminal = run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled'
  const end = isTerminal ? new Date(run.updatedAt).getTime() : Date.now()
  return formatDuration(end - start)
}

function relockPointer(): void {
  document.querySelector('canvas')?.requestPointerLock()
}

function transcriptLine(e: TranscriptEvent): { text: string; isTool: boolean } {
  const isTool = e.type === 'tool_call' || e.type === 'tool_info'
  if (isTool) {
    let name = e.content
    try {
      name = (JSON.parse(e.content) as { name?: string }).name ?? e.content
    } catch { /* not JSON — show raw */ }
    return { text: `⚙ ${name}`, isTool: true }
  }
  return { text: e.content, isTool: false }
}

/**
 * Right-side run inspector panel. Tab toggles it open on the nearest station's
 * run (within 8u) while playing; Tab/Esc closes and re-locks the pointer.
 * Detail is requested once through the session-owned remote-state store.
 */
export function RunInspector() {
  const inspectorRunId = useArenaStore((s) => s.inspectorRunId)
  const runs           = useArenaStore((s) => s.runs)
  const runDetails     = useArenaStore((s) => s.runDetailsById)
  const loadRunDetail  = useArenaStore((s) => s.loadRunDetail)
  const transcripts    = useArenaStore((s) => s.transcriptsByRun)

  const termRef = useRef<HTMLDivElement>(null)

  // Tab toggles (open gated in the store action); Esc closes.
  useEffect(() => {
    return modalInputRouter.register(INPUT_PRIORITY.inspector, (e) => {
      const st = useArenaStore.getState()
      if (e.key === 'Tab') {
        const before = st.inspectorRunId
        st.toggleInspector()
        const after = useArenaStore.getState().inspectorRunId
        if (before === null && after !== null) {
          e.preventDefault()
          document.exitPointerLock?.()
        } else if (before !== null && after === null) {
          e.preventDefault()
          relockPointer()
        }
        return true
      } else if (e.key === 'Escape' && st.inspectorRunId !== null) {
        st.closeInspector()
        relockPointer()
        return true
      }
      return st.inspectorRunId !== null && (e.code === 'KeyE' || e.code === 'KeyM')
    })
  }, [])

  useEffect(() => {
    if (inspectorRunId !== null) void loadRunDetail(inspectorRunId)
  }, [inspectorRunId, loadRunDetail])

  const base = inspectorRunId !== null ? runs.find((r) => r.id === inspectorRunId) : undefined
  const detail = inspectorRunId !== null ? runDetails[inspectorRunId] : undefined
  const view: NormalizedRun | undefined = detail ? { ...base, ...detail } : base
  const events = inspectorRunId !== null ? (transcripts[inspectorRunId] ?? []) : []

  // Autoscroll transcript to bottom on new events.
  useEffect(() => {
    const el = termRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [events.length, inspectorRunId])

  if (inspectorRunId === null) return null

  injectRunInspectorStyles()

  return (
    <div className="ri-panel" data-testid="run-inspector">
      {view ? (
        <>
          <div className="ri-header">
            <div className="ri-title">{view.title}</div>
            <div className="ri-meta">
              <span className="ri-pill" style={{ background: statusColor(view.status) }}>
                {view.status.toUpperCase()}
              </span>
              <span className="ri-duration">{runDuration(view)}</span>
              <button
                className="ri-portal-link"
                data-testid="ri-portal-link"
                onClick={() => window.open(portalRunUrl(inspectorRunId), '_blank', 'noopener,noreferrer')}
              >
                Open in portal ↗
              </button>
            </div>
          </div>

          <div className="ri-section">
            <div className="ri-section-title">Phases</div>
            {view.phases.length === 0 && <div className="ri-empty">No phases yet</div>}
            {view.phases.map((p) => (
              <div className="ri-phase" key={p.id}>
                <span className="ri-dot" style={{ background: statusColor(p.status) }} />
                <span className="ri-phase-name">{p.title}</span>
                {typeof p.attempt === 'number' && p.attempt > 0 && (
                  <span className="ri-phase-attempt">×{p.attempt}</span>
                )}
                {typeof p.durationMs === 'number' && (
                  <span className="ri-phase-dur">{formatDuration(p.durationMs)}</span>
                )}
              </div>
            ))}
          </div>

          <div className="ri-section">
            <div className="ri-section-title">Decisions</div>
            {(view.decisions?.length ?? 0) === 0 && <div className="ri-empty">No decisions yet</div>}
            <div className="ri-decisions">
              {(view.decisions ?? []).map((d, i) => (
                <div className="ri-decision" key={i} data-testid="ri-decision">
                  <div className="ri-decision-head">
                    <span className="ri-decision-verdict" style={{ color: decisionColor(d.decision) }}>
                      {d.decision.toUpperCase()}
                    </span>
                    <span className="ri-decision-phase">{d.phaseId}</span>
                    <span className="ri-decision-risk" style={{ color: riskColor(d.risk), borderColor: riskColor(d.risk) }}>
                      {d.risk}
                    </span>
                    <span className="ri-decision-conf">{Math.round(d.confidence * 100)}%</span>
                  </div>
                  <div className="ri-decision-reason">{d.reason}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="ri-section">
            <div className="ri-section-title">Checkpoints</div>
            {(view.checkpoints?.length ?? 0) === 0 && <div className="ri-empty">No checkpoints yet</div>}
            {(view.checkpoints ?? []).map((c) => (
              <div className="ri-checkpoint" key={c.number} data-testid="ri-checkpoint">
                <span className="ri-checkpoint-num">#{c.number}</span>
                <span className="ri-checkpoint-phase">{c.phaseId}</span>
                <span className="ri-checkpoint-reason">{c.reason}</span>
                <span className="ri-checkpoint-time">{formatTime(c.timestamp)}</span>
              </div>
            ))}
          </div>

          <div className="ri-section">
            <div className="ri-section-title">Live transcript</div>
            <div className="ri-term" ref={termRef} data-testid="ri-transcript">
              {events.length === 0 && <div className="ri-empty">Waiting for events…</div>}
              {events.map((e) => {
                const line = transcriptLine(e)
                return (
                  <div key={e.id} className={line.isTool ? 'ri-term-tool' : undefined}>
                    {line.text}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="ri-empty">Loading run…</div>
      )}
      <div className="ri-footer">
        <span className="ri-kbd">Tab</span> / <span className="ri-kbd">Esc</span> close
      </div>
    </div>
  )
}

// ── Injected styles (UX kit tokens: Nunito, cream/charcoal, status colors) ──

let stylesInjected = false

export function injectRunInspectorStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return
  stylesInjected = true
  const style = document.createElement('style')
  style.id = 'run-inspector-styles'
  style.textContent = `
.ri-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(400px, 92vw);
  z-index: 40;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem 1.25rem 0.75rem;
  overflow-y: auto;
  font-family: 'Nunito', system-ui, sans-serif;
  background: rgba(61, 64, 91, 0.96);
  border-left: 1px solid rgba(245, 235, 224, 0.15);
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.45);
  color: #f5ebe0;
}
.ri-header { display: flex; flex-direction: column; gap: 0.5rem; }
.ri-title {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.3;
  color: #f5ebe0;
}
.ri-meta { display: flex; align-items: center; gap: 0.5rem; }
.ri-pill {
  display: inline-block;
  padding: 1px 9px;
  border-radius: 9999px;
  font-weight: 800;
  font-size: 0.65rem;
  letter-spacing: 0.05em;
  color: #fff;
}
.ri-duration { font-size: 0.78rem; color: rgba(245, 235, 224, 0.6); }
.ri-portal-link {
  margin-left: auto;
  font-family: 'Nunito', system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  color: #f5ebe0;
  background: rgba(245, 235, 224, 0.1);
  border: 1px solid rgba(245, 235, 224, 0.2);
  border-radius: 9999px;
  padding: 0.1rem 0.6rem;
  cursor: pointer;
  white-space: nowrap;
}
.ri-portal-link:hover { background: rgba(245, 235, 224, 0.2); }
.ri-section { display: flex; flex-direction: column; gap: 0.375rem; }
.ri-section-title {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(245, 235, 224, 0.45);
}
.ri-empty { font-size: 0.78rem; color: rgba(245, 235, 224, 0.35); }
.ri-phase {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.82rem;
  padding: 0.15rem 0;
}
.ri-dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.ri-phase-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ri-phase-attempt { font-size: 0.72rem; color: #fbbf24; font-weight: 700; }
.ri-phase-dur { font-size: 0.72rem; color: rgba(245, 235, 224, 0.5); }
.ri-decisions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 14rem;
  overflow-y: auto;
}
.ri-decision {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(245, 235, 224, 0.08);
  border-radius: 0.5rem;
  padding: 0.5rem 0.625rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.ri-decision-head { display: flex; align-items: center; gap: 0.5rem; }
.ri-decision-verdict { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.04em; }
.ri-decision-phase {
  flex: 1;
  font-size: 0.72rem;
  color: rgba(245, 235, 224, 0.45);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ri-decision-risk {
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
  border: 1px solid;
  border-radius: 9999px;
  padding: 0 6px;
}
.ri-decision-conf { font-size: 0.72rem; color: rgba(245, 235, 224, 0.6); font-weight: 700; }
.ri-decision-reason {
  font-size: 0.78rem;
  line-height: 1.45;
  color: rgba(245, 235, 224, 0.8);
  overflow-wrap: break-word;
  white-space: normal;
}
.ri-checkpoint {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.78rem;
}
.ri-checkpoint-num { font-weight: 800; color: #b08968; }
.ri-checkpoint-phase { color: rgba(245, 235, 224, 0.8); }
.ri-checkpoint-reason { flex: 1; color: rgba(245, 235, 224, 0.5); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ri-checkpoint-time { color: rgba(245, 235, 224, 0.4); font-size: 0.7rem; }
.ri-term {
  background: #2f3247;
  color: #cbd5e1;
  border-radius: 0.5rem;
  padding: 0.5rem 0.625rem;
  font-family: ui-monospace, 'Cascadia Code', 'Fira Mono', monospace;
  font-size: 0.72rem;
  line-height: 1.5;
  max-height: 13rem;
  overflow-y: auto;
  overflow-wrap: break-word;
}
.ri-term-tool { color: #fbbf24; }
.ri-footer {
  margin-top: auto;
  padding-top: 0.5rem;
  text-align: center;
  font-size: 0.72rem;
  color: rgba(245, 235, 224, 0.4);
}
.ri-kbd {
  display: inline-block;
  background: rgba(245, 235, 224, 0.1);
  border: 1px solid rgba(245, 235, 224, 0.2);
  border-radius: 0.3rem;
  padding: 0 0.4rem;
  font-weight: 700;
  color: #f5ebe0;
}
`
  document.head.appendChild(style)
}
